import type { Ssm5TripTime as TripTime } from '@packages/domain/ssm5';

import { Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

/** How many decimals the time into the ignition cycle shows, in seconds. */
const TIME_DECIMALS = 3;

const MS_PER_SECOND = 1000;

/** Props for {@link Ssm5TripTime}. */
export type Ssm5TripTimeProps = {
    /** The ignition cycle and the time into it. */
    tripTime: TripTime;
};

const useStyles = createUseStyles({
    reading: {
        display: 'flex',
        gap: 4,
    },
    tripTime: {
        display: 'flex',
        flexShrink: 0,
        gap: 8,
    },
});

/**
 * An ignition cycle and the time into it, as the DTC panel reads them: "Trip: 576  Time: 41.800", the
 * time in seconds, and "Time: 0" at the start of the cycle. Each reading is its label and its value, set
 * apart as the design sets them.
 *
 * @param props - {@link Ssm5TripTimeProps}
 * @returns the trip and time
 */
export function Ssm5TripTime({ tripTime }: Ssm5TripTimeProps) {
    const classes = useStyles();
    const { t } = useTranslation();
    const readings = [
        { label: 'ssm5:dtcs.trip', value: String(tripTime.trip) },
        { label: 'ssm5:dtcs.time', value: toSeconds(tripTime.elapsedMs) },
    ];

    return (
        <div className={classes.tripTime}>
            {readings.map(({ label, value }) => (
                <div className={classes.reading} key={label}>
                    <Typography color="textDarkSecondary" variant="caption">
                        {t(label)}
                    </Typography>
                    <Typography color="textDarkSecondary" variant="caption">
                        {value}
                    </Typography>
                </div>
            ))}
        </div>
    );
}

/**
 * Write a time into the ignition cycle in seconds, as the design does: to the millisecond, and a bare 0 for
 * the start of the cycle.
 *
 * @param elapsedMs - the time into the ignition cycle, in milliseconds
 * @returns the time in seconds
 */
function toSeconds(elapsedMs: number): string {
    return elapsedMs === 0 ? '0' : (elapsedMs / MS_PER_SECOND).toFixed(TIME_DECIMALS);
}
